import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Col, Input, Row, Table } from 'reactstrap';
import Dropzone from 'react-dropzone';
import CheckIcon from 'mdi-react/CheckIcon';
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon';
import PhoneIcon from 'mdi-react/PhoneIcon';
import AccountClockOutlineIcon from 'mdi-react/AccountClockOutlineIcon';
import Loading from '../../../shared/components/custom/Loading';
import FormBox from '../../../shared/components/custom/FormBox';
import Api from '../../../util/api';
import readSpreadsheet from '../../../util/readSpreadsheet';
import exportToExcelFile from '../../../util/xlsx';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';

const p = partial('TierImport');
const s = partial('shared');

// Columns the import understands, and header names that usually hold them
// (sv/nb/en, compared without spaces, dashes or case)
const FIELDS = [
  {
    key: 'phone',
    names: ['phone', 'telefon', 'tel', 'mobil', 'mobile', 'cell', 'nummer'],
  },
  { key: 'email', names: ['email', 'epost', 'mail'] },
  {
    key: 'endDate',
    names: [
      'enddate',
      'duedate',
      'slutdatum',
      'sluttdato',
      'utgang',
      'utlop',
      'gallertill',
      'gyldigtil',
      'validuntil',
      'expires',
    ],
  },
  { key: 'name', names: ['name', 'namn', 'navn'] },
  {
    key: 'membershipNumber',
    names: ['membershipnumber', 'medlemsnummer', 'medlemsnr', 'memberid'],
  },
];
// Header names that usually hold a tier custom field of each type
const CUSTOM_FIELD_NAMES = {
  phone: ['phone', 'telefon', 'mobil', 'mobile'],
  firstname: ['firstname', 'fornamn', 'fornavn'],
  lastname: ['lastname', 'surname', 'efternamn', 'etternavn'],
  ssn: ['personnummer', 'personnr', 'fodselsnummer', 'ssn'],
  address: ['address', 'adress', 'adresse'],
  zipcode: ['zip', 'postalcode', 'postnummer', 'postnr'],
  city: ['city', 'postort', 'poststed', 'stad'],
};
const customFieldKey = (field) => `custom:${field.id}`;
const COUNTRIES = ['SE', 'NO', 'DK', 'FI'];
const CURRENCY_COUNTRY = { SEK: 'SE', NOK: 'NO', DKK: 'DK', EUR: 'FI' };

// The club's country from its settings (isoCountry), else from its currency,
// else Sweden
const clubCountry = (config) => {
  const iso = `${config?.isoCountry || ''}`.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(iso)) return iso;
  return CURRENCY_COUNTRY[config?.currencyCaps] || 'SE';
};
const OUTCOME_COLORS = {
  add: 'success',
  extend: 'info',
  pending: 'warning',
  duplicate: 'secondary',
  error: 'danger',
};
const OUTCOME_ORDER = ['error', 'duplicate', 'pending', 'extend', 'add'];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const isValidPhone = (value) => /^\+?[0-9]{6,15}$/.test(value.trim());

const simplify = (text) =>
  `${text}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ø/g, 'o')
    .replace(/[^a-z0-9]/g, '');

// The first header that looks like each field; -1 when none does. A tier
// custom field matches a header with its label, else one named like its type
const guessColumns = (headers, customFields) => {
  const simplified = headers.map(simplify);
  const find = (names) =>
    simplified.findIndex((header) =>
      names.some((name) => header.includes(name))
    );
  return {
    ...Object.fromEntries(FIELDS.map(({ key, names }) => [key, find(names)])),
    ...Object.fromEntries(
      customFields.map((field) => {
        const label = simplify(field.label || '');
        const byLabel = label ? simplified.indexOf(label) : -1;
        return [
          customFieldKey(field),
          byLabel >= 0 ? byLabel : find(CUSTOM_FIELD_NAMES[field.type] || []),
        ];
      })
    ),
  };
};

// Members to a tier from a CSV or Excel file: phone and/or email per row,
// end date from a column or the date field. The backend checks every row
// first (preview); people without an account get the tier when they join.
const ImportTierMembersPage = ({
  tierID,
  tier,
  getTierUsers,
  onUseOldUpload,
}) => {
  const config = useConfig();
  const customFields = useMemo(
    () => (tier?.customFields || []).filter((field) => field?.id),
    [tier]
  );
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState({});
  const [endDate, setEndDate] = useState('');
  const [country, setCountry] = useState(() => clubCountry(config));
  const [countryChosen, setCountryChosen] = useState(false);
  // The club's settings load after the page; follow them until a country is
  // picked by hand
  useEffect(() => {
    if (!countryChosen) setCountry(clubCountry(config));
  }, [config, countryChosen]);
  const countryOptions = COUNTRIES.includes(clubCountry(config))
    ? COUNTRIES
    : [clubCountry(config), ...COUNTRIES];
  const [sendPush, setSendPush] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [quickPhone, setQuickPhone] = useState('');
  const [quickEmail, setQuickEmail] = useState('');
  const [quickEndDate, setQuickEndDate] = useState('');
  const [quickResult, setQuickResult] = useState(null);

  const quickPhoneError =
    quickPhone.trim() && !isValidPhone(quickPhone) ? p('invalidPhone') : '';
  const quickEmailError =
    quickEmail.trim() && !isValidEmail(quickEmail) ? p('invalidEmail') : '';
  const canQuickAdd =
    (quickPhone.trim() || quickEmail.trim()) &&
    quickEndDate &&
    !quickPhoneError &&
    !quickEmailError;

  const runQuickAdd = async () => {
    setLoading(true);
    setQuickResult(null);
    try {
      const imported = await Api.tiers.importMembers({
        tierId: tierID,
        defaultCountry: country,
        sendPushNotification: sendPush,
        rows: [
          {
            phone: quickPhone.trim() || undefined,
            email: quickEmail.trim() || undefined,
            endDate: quickEndDate,
          },
        ],
      });
      setQuickResult(imported.rows[0]);
      if (['add', 'extend', 'pending'].includes(imported.rows[0]?.outcome)) {
        toast.success(p('importDone'));
        setQuickPhone('');
        setQuickEmail('');
        setQuickEndDate('');
        if (getTierUsers) getTierUsers();
      }
    } catch (err) {
      toast.error(p('importFailed'));
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (selected) => {
    setPreview(null);
    setResult(null);
    if (!selected) {
      setFile(null);
      return;
    }
    setLoading(true);
    try {
      const read = await readSpreadsheet(selected);
      if (!read.rows.length) {
        toast.error(p('emptyFile'));
        setFile(null);
      } else {
        setFile({ name: selected.name, ...read });
        setColumns(guessColumns(read.headers, customFields));
      }
    } catch (err) {
      toast.error(p('readFailed'));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const cell = (row, key) => (columns[key] >= 0 ? row[columns[key]] || '' : '');

  const payload = () => ({
    tierId: tierID,
    defaultEndDate: endDate || undefined,
    defaultCountry: country,
    sendPushNotification: sendPush,
    rows: file.rows.map((row) => {
      const extraData = {
        ...(cell(row, 'name') && { name: cell(row, 'name') }),
        ...(cell(row, 'membershipNumber') && {
          membershipNumber: cell(row, 'membershipNumber'),
        }),
      };
      const answers = Object.fromEntries(
        customFields
          .map((field) => [field.id, `${cell(row, customFieldKey(field))}`])
          .filter(([, value]) => value.trim())
      );
      return {
        phone: cell(row, 'phone') || undefined,
        email: cell(row, 'email') || undefined,
        endDate: cell(row, 'endDate') || undefined,
        ...(Object.keys(extraData).length && { extraData }),
        ...(Object.keys(answers).length && { customFields: answers }),
      };
    }),
  });

  const hasContactColumn = columns.phone >= 0 || columns.email >= 0;
  const canCheck =
    !!file && hasContactColumn && (endDate || columns.endDate >= 0);

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      setPreview(await Api.tiers.previewImport(payload()));
    } catch (err) {
      toast.error(p('previewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    setLoading(true);
    try {
      const imported = await Api.tiers.importMembers(payload());
      setResult(imported);
      setPreview(null);
      toast.success(p('importDone'));
      if (getTierUsers) getTierUsers();
    } catch (err) {
      toast.error(p('importFailed'));
    } finally {
      setLoading(false);
    }
  };

  // The rows that weren't imported, with the file's own columns and the reason
  const downloadProblems = (rows) => {
    const problems = rows.filter((row) =>
      ['error', 'duplicate'].includes(row.outcome)
    );
    exportToExcelFile(
      problems.map((row) => ({
        ...Object.fromEntries(
          file.headers.map((header, index) => [
            header || `#${index + 1}`,
            file.rows[row.row - 1]?.[index] ?? '',
          ])
        ),
        [p('reason')]:
          row.outcome === 'duplicate'
            ? p('outcome_duplicate')
            : p(`error_${row.error}`),
      })),
      `${(file.name || 'import').replace(/\.[^.]+$/, '')}-${p('problemsFile')}`
    );
  };

  const shown = result || preview;
  const sortedRows = useMemo(
    () =>
      shown
        ? [...shown.rows].sort(
            (a, b) =>
              OUTCOME_ORDER.indexOf(a.outcome) -
                OUTCOME_ORDER.indexOf(b.outcome) || a.row - b.row
          )
        : [],
    [shown]
  );
  const importable = preview
    ? preview.summary.add + preview.summary.extend + preview.summary.pending
    : 0;

  return (
    <div className="form m-3" style={{ flexDirection: 'column' }}>
      <Loading loading={loading} />
      <Row>
        <Col lg={12}>
          <Alert color="info">
            <strong>{p('title')}</strong>
            <ul
              className="mt-2 mb-0"
              style={{ listStyle: 'none', paddingLeft: 0 }}
            >
              <li className="d-flex align-items-start mb-1">
                <FileDocumentOutlineIcon
                  size={18}
                  style={{ marginRight: 8, flexShrink: 0 }}
                />
                <span>{p('infoPointFile')}</span>
              </li>
              <li className="d-flex align-items-start mb-1">
                <PhoneIcon
                  size={18}
                  style={{ marginRight: 8, flexShrink: 0 }}
                />
                <span>{p('infoPointContact')}</span>
              </li>
              <li className="d-flex align-items-start">
                <AccountClockOutlineIcon
                  size={18}
                  style={{ marginRight: 8, flexShrink: 0 }}
                />
                <span>{p('infoPointPending')}</span>
              </li>
            </ul>
          </Alert>
        </Col>
      </Row>

      <FormBox title={p('quickAddTitle')} className="mb-3">
        <Row className="px-2 align-items-end">
          <Col md={3} className="mb-2">
            <div className="form__form-group">
              <span className="form-header">{p('field_phone')}</span>
              <div className="marginTop-5" />
              <Input
                bsSize="sm"
                invalid={!!quickPhoneError}
                value={quickPhone}
                onChange={(e) => setQuickPhone(e.target.value)}
                placeholder="+46701234567"
              />
              <span
                className="form__form-group-error"
                style={{ visibility: quickPhoneError ? 'visible' : 'hidden' }}
              >
                {quickPhoneError || ' '}
              </span>
            </div>
          </Col>
          <Col md={3} className="mb-2">
            <div className="form__form-group">
              <span className="form-header">{p('field_email')}</span>
              <div className="marginTop-5" />
              <Input
                type="email"
                bsSize="sm"
                invalid={!!quickEmailError}
                value={quickEmail}
                onChange={(e) => setQuickEmail(e.target.value)}
              />
              <span
                className="form__form-group-error"
                style={{ visibility: quickEmailError ? 'visible' : 'hidden' }}
              >
                {quickEmailError || ' '}
              </span>
            </div>
          </Col>
          <Col md={3} className="mb-2">
            <div className="form__form-group">
              <span className="form-header">{p('field_endDate')}</span>
              <div className="marginTop-5" />
              <Input
                type="date"
                bsSize="sm"
                value={quickEndDate}
                onChange={(e) => setQuickEndDate(e.target.value)}
              />
              <span
                className="form__form-group-error"
                style={{ visibility: 'hidden' }}
              >
                {' '}
              </span>
            </div>
          </Col>
          <Col md={3} className="mb-2 d-flex flex-column">
            <Button
              color="primary"
              size="sm"
              disabled={!canQuickAdd}
              onClick={runQuickAdd}
            >
              {p('quickAdd')}
            </Button>
            <span
              className="form__form-group-error"
              style={{ visibility: 'hidden' }}
            >
              {' '}
            </span>
          </Col>
        </Row>
        {!quickPhone.trim() &&
          !quickEmail.trim() &&
          quickEndDate &&
          !quickPhoneError &&
          !quickEmailError && (
            <p className="text-danger px-2 mb-0">{p('needPhoneOrEmail')}</p>
          )}
        {quickResult && (
          <Row className="px-2 mt-1">
            <Col>
              <Badge color={OUTCOME_COLORS[quickResult.outcome]}>
                {p(`outcome_${quickResult.outcome}`)}
              </Badge>{' '}
              {quickResult.error && (
                <small className="text-danger">
                  {p(`error_${quickResult.error}`)}
                </small>
              )}
              {[...new Set(quickResult.warnings || [])].map((warning) => (
                <small key={warning} className="text-muted ml-1">
                  {p(`warning_${warning}`)}
                </small>
              ))}
            </Col>
          </Row>
        )}
      </FormBox>

      <Row className="mb-3">
        <Col md={12}>
          <div className="dropzone--container mb-0">
            <div
              className="dropzone dropzone--single dropzone--custom-height"
              style={{ background: '#f7f8fa', borderRadius: 8 }}
            >
              <Dropzone
                className="dropzone__input"
                accept=".csv,.txt,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple={false}
                onDrop={([selected]) => onFile(selected)}
              >
                <div
                  className="dropzone__drop-here"
                  style={{ textAlign: 'center', padding: 20 }}
                >
                  {file ? (
                    <>
                      <FileDocumentOutlineIcon size={48} />
                      <div style={{ fontWeight: 600, marginTop: 10 }}>
                        {file.name}
                      </div>
                      <small className="text-muted">
                        {p('fileSummary', {
                          name: file.name,
                          count: file.rows.length,
                        })}
                      </small>
                      <br />
                      <small className="text-muted">
                        {p('dropFilesToReplace')}
                      </small>
                    </>
                  ) : (
                    <>
                      <img
                        height="48"
                        src="/assets/icons/upload.svg"
                        alt="upload"
                      />
                      <div style={{ fontWeight: 600, marginTop: 10 }}>
                        {p('dropFilesToUpload')}
                      </div>
                      <small className="text-muted">{p('file')}</small>
                    </>
                  )}
                </div>
              </Dropzone>
            </div>
            {file && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 6,
                }}
              >
                <Button
                  size="sm"
                  color="danger"
                  outline
                  className="mb-0"
                  onClick={(e) => {
                    e.preventDefault();
                    onFile(null);
                  }}
                >
                  {s('remove')}
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {file && (
        <>
          <FormBox title={p('columns')} className="mb-3">
            <Row className="px-2">
              {[
                ...FIELDS.map(({ key }) => ({ key, label: p(`field_${key}`) })),
                ...customFields.map((field) => ({
                  key: customFieldKey(field),
                  label: `${field.label || field.type}${field.required ? ' *' : ''}`,
                })),
              ].map(({ key, label }) => (
                <Col md={4} lg={2} key={key} className="mb-2">
                  <div className="form__form-group">
                    <span className="form-header">{label}</span>
                    <div className="marginTop-5" />
                    <Input
                      type="select"
                      bsSize="sm"
                      value={columns[key] ?? -1}
                      onChange={(e) =>
                        setColumns({
                          ...columns,
                          [key]: Number(e.target.value),
                        })
                      }
                    >
                      <option value={-1}>{p('notInFile')}</option>
                      {file.headers.map((header, index) => (
                        <option key={`${key}-${index}`} value={index}>
                          {header || `#${index + 1}`}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              ))}
            </Row>
            {!hasContactColumn && (
              <p className="text-danger px-2 mb-0">{p('needPhoneOrEmail')}</p>
            )}
            {customFields.length > 0 && (
              <small className="text-muted px-2">{p('customFieldsHint')}</small>
            )}
          </FormBox>

          <Row className="mb-3">
            <Col md={4} lg={3}>
              <div className="form__form-group">
                <span className="form-header">{p('defaultEndDate')}</span>
                <div className="marginTop-5" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <small className="text-muted">
                  {columns.endDate >= 0
                    ? p('defaultEndDateHintColumn')
                    : p('defaultEndDateHint')}
                </small>
              </div>
            </Col>
            <Col md={4} lg={3}>
              <div className="form__form-group">
                <span className="form-header">{p('country')}</span>
                <div className="marginTop-5" />
                <Input
                  type="select"
                  bsSize="sm"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setCountryChosen(true);
                  }}
                >
                  {countryOptions.map((code) => (
                    <option key={code} value={code}>
                      {COUNTRIES.includes(code) ? p(`country_${code}`) : code}
                    </option>
                  ))}
                </Input>
                <small className="text-muted">{p('countryHint')}</small>
              </div>
            </Col>
            <Col md={4} lg={3} className="d-flex align-items-center">
              <label
                className="checkbox-btn checkbox-btn--colored-click mt-3"
                htmlFor="import-send-push"
              >
                <input
                  className="checkbox-btn__checkbox"
                  type="checkbox"
                  id="import-send-push"
                  checked={sendPush}
                  onChange={(e) => setSendPush(e.target.checked)}
                />
                <span className="checkbox-btn__checkbox-custom">
                  <CheckIcon />
                </span>
                <span className="checkbox-btn__label">{p('sendPush')}</span>
              </label>
            </Col>
          </Row>

          <Button
            color="primary"
            size="sm"
            disabled={!canCheck}
            onClick={runPreview}
          >
            {p('check')}
          </Button>
        </>
      )}

      {shown && (
        <div className="mt-4">
          <h5>{result ? p('resultTitle') : p('previewTitle')}</h5>
          <div className="mb-3">
            {Object.entries(shown.summary).map(([outcome, count]) => (
              <Badge
                key={outcome}
                color={OUTCOME_COLORS[outcome]}
                className="mr-2 p-2"
              >
                {p(`outcome_${outcome}`)}: {count}
              </Badge>
            ))}
          </div>
          {preview && (
            <Button
              color="success"
              size="sm"
              disabled={!importable}
              onClick={runImport}
              className="mr-2"
            >
              {p('import', { count: importable })}
            </Button>
          )}
          {shown.summary.error + shown.summary.duplicate > 0 && (
            <Button
              size="sm"
              outline
              onClick={() => downloadProblems(shown.rows)}
            >
              {p('downloadProblems')}
            </Button>
          )}
          <Table responsive bordered size="sm" className="mt-3">
            <thead>
              <tr>
                <th>{p('row')}</th>
                <th>{p('field_phone')}</th>
                <th>{p('field_email')}</th>
                <th>{p('field_endDate')}</th>
                <th>{s('status')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.slice(0, 500).map((row) => (
                <tr key={row.row}>
                  <td>{row.row}</td>
                  <td>{row.phone || ''}</td>
                  <td>{row.email || ''}</td>
                  <td>{row.endDate || ''}</td>
                  <td>
                    <Badge color={OUTCOME_COLORS[row.outcome]}>
                      {p(`outcome_${row.outcome}`)}
                    </Badge>{' '}
                    {row.error && (
                      <small className="text-danger">
                        {p(`error_${row.error}`)}
                      </small>
                    )}
                    {[...new Set(row.warnings || [])].map((warning) => (
                      <small key={warning} className="text-muted ml-1">
                        {p(`warning_${warning}`)}
                      </small>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {sortedRows.length > 500 && (
            <small className="text-muted">
              {p('moreRows', { count: sortedRows.length - 500 })}
            </small>
          )}
        </div>
      )}
      {onUseOldUpload && (
        <Row className="mt-4">
          <Col className="text-center">
            <a
              href="#"
              role="button"
              className="text-muted"
              style={{ textDecoration: 'underline', fontSize: 13 }}
              onClick={(e) => {
                e.preventDefault();
                onUseOldUpload();
              }}
            >
              {p('useOldUpload')}
            </a>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ImportTierMembersPage;
